/**
 * Контракты генератора упражнений.
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryUnit,
  TrainingMode,
} from '@type2x/shared-contracts';
import type { MaterialMix } from '@type2x/learning-engine';

/** Источник, из которого попал элемент упражнения. */
export const ITEM_ORIGINS = ['weak', 'due', 'fresh', 'maintenance', 'transfer'] as const;
export type ItemOrigin = (typeof ITEM_ORIGINS)[number];

/** Один элемент упражнения. */
export interface ExerciseItem {
  readonly text: string;
  readonly origin: ItemOrigin;
  /** Учебная единица, ради которой элемент включён; null для контрольного материала. */
  readonly unitId: string | null;
}

/** Готовое упражнение. */
export interface Exercise {
  readonly seed: string;
  readonly mode: TrainingMode;
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly items: readonly ExerciseItem[];
  /** Текст для набора: элементы, соединённые пробелами. */
  readonly text: string;
  readonly hintLevel: HintLevel;
  /** Фактически использованные доли материала. */
  readonly mix: MaterialMix;
}

/** Материал, доступный генератору. */
export interface CandidatePools {
  /** Слабые единицы, отсортированные по убыванию приоритета. */
  readonly weak: readonly MasteryUnit[];
  /** Единицы, срок повторения которых наступил. */
  readonly due: readonly MasteryUnit[];
  /** Новый, ещё не показанный материал. */
  readonly fresh: readonly MasteryUnit[];
  /** Освоенный материал для поддержания автоматизма. */
  readonly maintenance: readonly MasteryUnit[];
  /** Контрольный материал, не пересекающийся с тренировочным. */
  readonly transfer: readonly MasteryUnit[];
}

/** Запрос на генерацию упражнения. */
export interface ExerciseRequest {
  readonly seed: string;
  readonly mode: TrainingMode;
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /** Сколько элементов должно быть в упражнении. */
  readonly itemCount: number;
  readonly mix: MaterialMix;
  readonly pools: CandidatePools;
  readonly hintLevel: HintLevel;
  /** Символы, разрешённые в этом режиме. Пусто — ограничений нет. */
  readonly allowedCharacters?: ReadonlySet<string>;
  /** Максимум показов одного текста подряд. */
  readonly maxConsecutiveRepeats: number;
}
